import { StripeError } from '@stripe/stripe-js';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import {
  OrderProcessBottomNavBar,
  OrderProcessDetails,
  OrderProcessOwner,
  OrderProcessPayment,
  OrderStepsIndicator,
} from '../../components/order-process';
import { IOrderProcessDetailsHandle } from '../../components/order-process/OrderProcessDetails';
import { IOrderProcessOwnerHandle } from '../../components/order-process/OrderProcessOwner';
import { IOrderProcessPaymentHandle } from '../../components/order-process/OrderProcessPayment';
import { IOrderStep } from '../../components/order-process/OrderStepsIndicator';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IAddress,
  IBankWireInformation,
  ICheckoutPage,
  IClass,
  ICountry,
  ICurrency,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IOrder,
  ISubClass,
  OrderStatus,
  ServiceType,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { logError, logInfo } from '../../services/log.service';
import {
  existData,
  getStoredData,
  removeStoredData,
  storeData,
} from '../../services/storage.service';
import { getRoutes } from '../../shared/useBuild';
import {
  createCheckoutBankWirePayment,
  createCheckoutOrder,
  getOrderById,
  sendBeginCheckoutEvent,
  updateCheckoutOrder,
} from '../../shared/useCheckout';
import {
  buildApiKey,
  getCommonTranslations,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
  mapAppOrder,
} from '../../shared/useUtils';
import {
  AppOrder,
  IAppOrder,
  IAppOrderClass,
  IAppOrderCountry,
} from '../../store/models/store.models';
import {
  IAppPage,
  setIsLoading,
  setPendingOrdersCount,
} from '../../store/slices/appSlice';
import { setCurrentOrder } from '../../store/slices/currentOrderSlice';
import { NextPageWithLayout } from '../_app';

const pageServiceType: ServiceType = ServiceType.SEARCH;

interface ITrademarkSearchPageProps extends ILayoutPageProps {
  countries: ICountry[];
  classes: IClass[];
  billingBankWireInformation: IBankWireInformation[];
  labels: {
    stepOneTitle: string;
    stepTwoTitle: string;
    stepThreeTitle: string;
    countrySelectionTitle: string;
    countrySelectionPlaceholder: string;
    countrySelectionAddButton: string;
    trademarkInformationTitle: string;
    trademarkInformationPlaceholder: string;
    uploadLogoTip: string;
    goodsAndServicesTitle: string;
    goodsAndServicesDescription: string;
    goodsAndServicesNotice: string;
    goodsAndServicesButtonLabel: string;
    goodsAndServicesButtonTip: string;
    ownerTitle: string;
    ownerDescription: string;
    addressesEmptyPlaceholder: string;
    ownerCompanyLabel: string;
    ownerCompanyPlaceholder: string;
    ownerTaxIdLabel: string;
    ownerTaxIdPlaceholder: string;
    ownerAddressesTitle: string;
    ownerAddAddressButton: string;
    orderSummaryTitle: string;
    billingTitle: string;
    billingCreditCardTabName: string;
    billingBankWireTabName: string;
    billingBankWireNotice: string;
    billingBankWireNoticeLink: string;
    billingBankWireNoticeLinkLabel: string;
    billingBankWireBeneficiaryLabel: string;
    billingBankWireBeneficiary: string;
    billingBankWireAddressLabel: string;
    billingBankWireAddress: string;
    totalLabel: string;
    backButton: string;
    nextButton: string;
    payButton: string;
    goodsAndServicesClassLabel: string;
    addressSaveSuccessMessage: string;
    addressSaveErrorMessage: string;
    addressDeletePromptMessage: string;
    addressDeleteSuccessMessage: string;
    addressDeleteErrorMessage: string;
    addressEditButton: string;
    addressDeleteButton: string;
    countryRemoveButton: string;
    uploadLogoTitle: string;
    uploadLogoRemoveButton: string;
    goodsAndServicesSelectedTitle: string;
    goodsAndServicesRemoveButton: string;
    goodsAndServicesModalTitle: string;
    goodsAndServicesModalDescription: string;
    goodsAndServicesModalCancelButton: string;
    goodsAndServicesModalDoneButton: string;
    goodsAndServicesClassDescripionPlaceholder: string;
    goodsAndServicesSearchPlaceholder: string;
    billingBankWireClipboardTip: string;
    termsCheckboxLabel: string;
    billingPaymentMethodsTitle: string;
    addressBookCreateOrModify: {
      aliasLabel?: string;
      aliasPlaceholder: string;
      nameLabel?: string;
      namePlaceholder: string;
      addressLabel?: string;
      addressPlaceholder: string;
      postCodeLabel?: string;
      postCodePlaceholder: string;
      cityLabel?: string;
      cityPlaceholder: string;
      countryLabel?: string;
      countryPlaceholder: string;
      referenceSectionTitle: string;
      nameSectionTitle: string;
      addressSectionTitle: string;
      taxIdLabel?: string;
      taxIdPlaceholder?: string;
      title: string;
      saveButton: string;
      deleteButton: string;
    };
    formValidation: {
      requiredField: string;
      termsAndConditionsMustBeAccepted: string;
    };
    showMore: string;
    showLess: string;
    noServiceAvailableMessage: string;
  };
  canonicalLink: string;
  alternativeLinks: { hrefLang: string; href: string }[];
}

interface ITrademarkSearchPageState {
  currentStep: number;
  currentPaymentMethod: 'credit-card' | 'bank-wire';
}

const TrademarkSearchPage: NextPageWithLayout<ITrademarkSearchPageProps> = (
  props: ITrademarkSearchPageProps
) => {
  const dispatch = useAppDispatch();
  const appState = useAppSelector((state) => state.app);
  const appPages = useAppSelector((state) => state.app.pages);
  const user = useAppSelector((state) => state.auth.user);
  const currentOrderState = useAppSelector((state) => state.currentOrder);
  const orderStepsIndicatorSteps = useMemo<IOrderStep[]>(
    () => [
      {
        id: 1,
        name: 'details',
        label: props.labels.stepOneTitle,
      },
      {
        id: 2,
        name: 'owner',
        label: props.labels.stepTwoTitle,
      },
      {
        id: 3,
        name: 'payment',
        label: props.labels.stepThreeTitle,
      },
    ],
    [
      props.labels.stepOneTitle,
      props.labels.stepThreeTitle,
      props.labels.stepTwoTitle,
    ]
  );
  const initialPageState: ITrademarkSearchPageState = {
    currentStep: 1,
    currentPaymentMethod: 'credit-card',
  };
  const [state, setState] = useState(initialPageState);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [classes, setClasses] = useState<IAppOrderClass[]>([]);
  const router = useRouter();
  const orderProcessDetailsRef = useRef<IOrderProcessDetailsHandle>();
  const orderProcessOwnerRef = useRef<IOrderProcessOwnerHandle>();
  const orderProcessPaymentRef = useRef<IOrderProcessPaymentHandle>();

  const getRoute = useCallback(
    (actualPath: string) => {
      return (
        appPages?.find(
          (x: IAppPage) =>
            x && x.destination === actualPath && x.language === router.locale
        )?.source || actualPath
      );
    },
    [appPages, router.locale]
  );

  const mapCountries = useCallback((countries: ICountry[], locale: string) => {
    return countries
      .map(
        (x) =>
          ({
            id: x.id,
            name: getTranslation(x.nameTranslations, locale).text as string,
            code: x.iso.toLowerCase(),
          } as IAppOrderCountry)
      )
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
  }, []);

  const mapClasses = useCallback((classes: IClass[], locale: string) => {
    return classes.map(
      (x) =>
        ({
          id: x.id,
          name: getTranslation(x.descriptionTranslations, locale)
            .text as string,
          code: x.code,
          subClasses: x.subClasses?.map((y) => ({
            subClassId: y.subClassId,
            description: getTranslation(y.descriptionTranslations, locale),
          })),
          isCollapsed: true,
        } as IAppOrderClass)
    );
  }, []);

  const getCountryData = useCallback(async (iso: string) => {
    const countriesUrlParams = {
      'populate[0]': 'searchClassPrices',
      'populate[1]': 'applicationClassPrices',
      'populate[2]': 'finalFees',
      'fields[0]': 'id',
      'fields[1]': 'iso',
      'filters[iso][$eqi]': iso,
    };
    const countries = flatStrapiCollection(
      await fetchStrapi<any>(`/countries`, countriesUrlParams)
    ) as ICountry[];

    const cleanUpCountries = (countries: ICountry[]) => {
      return countries.map((x) => {
        const country = { ...x };
        country.searchClassPrices = x.searchClassPrices.map((y) => {
          const price = { ...y };
          delete price.valueName;
          return price;
        });
        country.applicationClassPrices = x.applicationClassPrices.map((y) => {
          const price = { ...y };
          delete price.valueName;
          return price;
        });
        country.finalFees = x.finalFees.map((y) => {
          const price = { ...y };
          delete price.valueName;
          return price;
        });
        return country;
      }) as any[];
    };

    return cleanUpCountries(countries)[0];
  }, []);

  const populateCountryData = useCallback(
    async (iso: string) => {
      const countryData = await getCountryData(iso);
      return countryData;
    },
    [getCountryData]
  );

  const handleStepChange = useCallback(
    (step: number) => {
      state.currentStep = step;
      setState({ ...state });
    },
    [state]
  );

  const handlePrev = useCallback(() => {
    // Set prev step
    handleStepChange(state.currentStep - 1);
  }, [handleStepChange, state.currentStep]);

  const getOrders = useCallback(async () => {
    try {
      if (user?.id) {
        const ordersResponse: IOrder[] = await fetchStrapi('/orders/mine');
        const pendingOrders: IOrder[] = ordersResponse.filter(
          (x) => x.status === 'draft'
        );

        dispatch(setPendingOrdersCount(pendingOrders.length));
        logInfo(['Response', pendingOrders], 'App → getOrders');
      }
    } catch (error) {
      logError(['Error', error], 'App → getOrders');
    }
  }, [dispatch, user?.id]);

  const createOrder = useCallback(
    async (order: IOrder, ownerAddress?: IAddress) => {
      const newOrder = await createCheckoutOrder(
        order,
        currentOrderState,
        pageServiceType,
        ownerAddress
      );
      const currentCurrentOrderState = { ...currentOrderState };
      currentCurrentOrderState.strapiOrderId = newOrder.id;
      currentCurrentOrderState.currency = appState.currentCurrency;
      dispatch(setCurrentOrder(currentCurrentOrderState));
    },
    [appState.currentCurrency, currentOrderState, dispatch]
  );

  const updateOrder = useCallback(
    async (orderId: number, order: IOrder, ownerAddress?: IAddress) => {
      await updateCheckoutOrder(
        orderId,
        order,
        currentOrderState,
        pageServiceType,
        ownerAddress
      );
    },
    [currentOrderState]
  );

  const handleUpsertOrder = useCallback(async () => {
    const myAddresses: IAddress[] = await fetchStrapi('/addresses/mine');
    const ownerAddress: IAddress = myAddresses.find(
      (x) => x.id === currentOrderState.addressId
    ) as IAddress;
    const billingAddress: IAddress = myAddresses.find(
      (x) => x.id === currentOrderState.billingAddressId
    ) as IAddress;

    const order: IOrder = {
      user: user?.id as number,
      billingAddress: billingAddress?.address,
      billingCity: billingAddress?.city,
      billingName: billingAddress?.name,
      billingPostcode: billingAddress?.postCode,
      billingTitle: billingAddress?.alias,
      billingTaxId: billingAddress?.taxId,
      billingCountry: billingAddress?.country,
      currency: appState.currentCurrency?.id,
      status: OrderStatus.DRAFT,
      totalPrice: currentOrderState.totalPrice,
    };

    try {
      if (currentOrderState.strapiOrderId) {
        // Update
        await updateOrder(currentOrderState.strapiOrderId, order, ownerAddress);
      } else {
        // Create
        await createOrder(order, ownerAddress);
      }

      await getOrders();
    } catch (error) {
      console.error(error);
    }
  }, [
    appState.currentCurrency?.id,
    createOrder,
    currentOrderState.addressId,
    currentOrderState.billingAddressId,
    currentOrderState.totalPrice,
    currentOrderState.strapiOrderId,
    updateOrder,
    user?.id,
    getOrders,
  ]);

  const populateAppOrderTrademarkCountries = useCallback(
    async (order: IAppOrder) => {
      // Identificar países que necesitan datos
      const countriesNeedingData = order.trademarkCountries.filter(
        (country) =>
          !country.searchClassPrices ||
          !country.applicationClassPrices ||
          !country.finalFees
      );

      // Si no hay países que necesiten datos, retornar la orden sin cambios
      if (countriesNeedingData.length === 0) {
        return order;
      }

      // Cargar datos de todos los países en paralelo
      const countriesDataPromises = countriesNeedingData.map((country) =>
        populateCountryData(country.code).then((countryData) => ({
          code: country.code,
          data: countryData,
        }))
      );

      const countriesData = await Promise.all(countriesDataPromises);

      // Aplicar todos los datos de una vez
      const modifiedOrder = {
        ...order,
        trademarkCountries: order.trademarkCountries.map((trademarkCountry) => {
          const countryData = countriesData.find(
            (cd) => cd.code === trademarkCountry.code
          );

          if (countryData) {
            return {
              ...trademarkCountry,
              searchClassPrices: countryData.data.searchClassPrices,
              applicationClassPrices: countryData.data.applicationClassPrices,
              finalFees: countryData.data.finalFees,
            };
          }

          return trademarkCountry;
        }),
      };

      return modifiedOrder;
    },
    [populateCountryData]
  );

  const initializeCurrentOrder = useCallback(async () => {
    // Set initial default order
    let order: IAppOrder = new AppOrder(pageServiceType);
    order.currency = appState.currentCurrency;

    if (!!user && router.query.orderId) {
      // Option 1. Start from an existing order by using orderId as Strapi entity id
      const remoteOrder = await getOrderById(
        parseInt(router.query.orderId as string, 10)
      );

      if (remoteOrder) {
        order = mapAppOrder(remoteOrder, user?.id as number, router.locale);
      }
    } else if (router.query.countries) {
      // Option 2. Start from scratch if countries come from query
      const countriesQuery = (router.query.countries as string)
        .split(',')
        .map((x) => x.toLowerCase());
      const selectedCountries = mapCountries(
        props.countries,
        router.locale || 'en'
      ).filter((x) => countriesQuery.includes(x.code));
      order.trademarkCountries = selectedCountries;
    } else if (!!user && existData('order')) {
      // Option 3. Restore order from local storage, then remove it from local storage
      order = getStoredData<IAppOrder>('order') as IAppOrder;
    }

    if (order.trademarkCountries.length > 0) {
      order = await populateAppOrderTrademarkCountries(order);
    }

    if (order.currency?.id !== appState.currentCurrency?.id) {
      order.currency = appState.currentCurrency;
    }

    dispatch(setCurrentOrder({ ...order }));

    let nextStep = 1;
    const storedNextStep = getStoredData<number>('checkout-step');

    if (!!storedNextStep) {
      nextStep = storedNextStep;
    }

    handleStepChange(nextStep);

    logInfo(['Restored order', order], 'TrademarkSearchPage');
  }, [
    appState.currentCurrency,
    dispatch,
    handleStepChange,
    mapCountries,
    populateAppOrderTrademarkCountries,
    props.countries,
    router.locale,
    router.query.countries,
    router.query.orderId,
    user,
  ]);

  const handlePay = useCallback(
    async (paymentMethod: 'credit-card' | 'bank-wire') => {
      if (paymentMethod === 'credit-card' && orderProcessPaymentRef.current) {
        await orderProcessPaymentRef.current.submitPayment();
      }

      if (paymentMethod === 'bank-wire') {
        await createCheckoutBankWirePayment(
          currentOrderState,
          appState.currentCurrency as ICurrency,
          user?.id as number
        );
      }
    },
    [appState.currentCurrency, currentOrderState, user?.id]
  );

  const clearStoredOrder = useCallback(() => {
    const storedOrder = getStoredData<IAppOrder>('order');

    if (!!storedOrder && storedOrder.id === currentOrderState.id) {
      removeStoredData('order');
      logInfo(
        ['Removed stored order', storedOrder],
        'TrademarkRegistrationPage'
      );
    }
  }, [currentOrderState]);

  const handleNext = useCallback(async () => {
    let canGoNext = false;

    if (state.currentStep === 1) {
      canGoNext =
        !!orderProcessDetailsRef.current &&
        orderProcessDetailsRef.current.validateStep();

      if (canGoNext) {
        sendBeginCheckoutEvent(currentOrderState, pageServiceType);

        if (!user) {
          // Store order in local storage to restore it before login
          storeData(currentOrderState, 'order');
          // Redirect to login page
          router.push(`${getRoute('/account/login')}`);
          return;
        } else {
          await handleUpsertOrder();
        }
      }
    }

    if (state.currentStep === 2) {
      canGoNext =
        !!orderProcessOwnerRef.current &&
        orderProcessOwnerRef.current.validateStep();
    }

    if (state.currentStep === 3) {
      canGoNext =
        !!orderProcessPaymentRef.current &&
        orderProcessPaymentRef.current.validateStep();

      if (canGoNext) {
        dispatch(setIsLoading(true));
        await handleUpsertOrder();
        await handlePay(state.currentPaymentMethod);
        dispatch(setIsLoading(false));
        router.push(getRoute('/bank-wire-checkout-result'));
      }

      return;
    }

    // Set next step
    if (canGoNext) {
      handleStepChange(state.currentStep + 1);
      window.scrollTo({ top: 0 });
      clearStoredOrder();
    }

    const storedNextStep = getStoredData<number>('checkout-step');

    if (!!storedNextStep) {
      removeStoredData('checkout-step');
    }
  }, [
    clearStoredOrder,
    currentOrderState,
    dispatch,
    getRoute,
    handlePay,
    handleStepChange,
    handleUpsertOrder,
    router,
    state.currentPaymentMethod,
    state.currentStep,
    user,
  ]);

  const handleOrderChange = useCallback(
    async (order: IAppOrder) => {
      let modifiedOrder = { ...order };
      if (order.trademarkCountries.length > 0) {
        modifiedOrder = await populateAppOrderTrademarkCountries(order);
      }
      modifiedOrder.currency = appState.currentCurrency;
      dispatch(setCurrentOrder(modifiedOrder));
    },
    [appState.currentCurrency, dispatch, populateAppOrderTrademarkCountries]
  );

  const getAddresses = useCallback(async () => {
    try {
      const userAddresses: IAddress[] = await fetchStrapi('/addresses/mine');
      logInfo(
        ['User addresses', userAddresses],
        'TrademarkSearchPage → getAddresses'
      );
      return userAddresses && userAddresses.length > 0 ? userAddresses : [];
    } catch (error) {
      logError(
        ['No user present', error],
        'TrademarkSearchPage → getAddresses'
      );
      throw error;
    }
  }, []);

  const getSubClasses = useCallback(async () => {
    const classesUrlParams = {
      'populate[0]': 'subClasses',
      'populate[1]': 'subClasses.descriptionTranslations',
      'pagination[start]': '0',
      'pagination[limit]': '-1',
    };

    const classes = flatStrapiCollection(
      await fetchStrapi<any>(`/classes`, classesUrlParams)
    ) as IClass[];
    return classes.map((x) => ({
      classCode: x.code,
      subClasses: x.subClasses,
    }));
  }, []);

  const populateClassesWithSubClasses = useCallback(
    (
      classes: IClass[],
      subClasses: { classCode: number; subClasses: ISubClass[] | undefined }[]
    ) => {
      return classes.map((x) => {
        const subClassesForClass = subClasses.find(
          (y) => y.classCode === x.code
        );
        x.subClasses = subClassesForClass?.subClasses;
        return x;
      });
    },
    []
  );

  const orderHasSomeCountryWithNoSearchClassPrices = useCallback(() => {
    return currentOrderState.trademarkCountries.some((x) => {
      return (
        !x.searchClassPrices ||
        x.searchClassPrices.length === 0 ||
        x.searchClassPrices.some((y) => y.value === 0)
      );
    });
  }, [currentOrderState.trademarkCountries]);

  useEffect(() => {
    initializeCurrentOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.countries, router.query.orderId, user]);

  useEffect(() => {
    if (!!user) {
      getAddresses().then((x) => {
        setAddresses(x);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    getSubClasses().then((x) => {
      const populatedClasses = populateClassesWithSubClasses(props.classes, x);
      const appOrderClasses = mapClasses(
        populatedClasses,
        router.locale || 'en'
      );
      setClasses(appOrderClasses);
    });
  }, [
    getSubClasses,
    mapClasses,
    populateClassesWithSubClasses,
    props,
    props.classes,
    router.locale,
  ]);

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <meta name="robots" content="noindex, nofollow" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          {/* Steps indicator */}
          <div className="is-text-align-center">
            <OrderStepsIndicator
              steps={orderStepsIndicatorSteps}
              currentStep={state.currentStep}
            />
          </div>

          {/* Step content */}
          <div>
            {(() => {
              switch (state.currentStep) {
                case 1:
                  return (
                    <OrderProcessDetails
                      countries={mapCountries(
                        props.countries,
                        router.locale || 'en'
                      )}
                      classes={classes}
                      labels={{ ...props.labels }}
                      order={currentOrderState}
                      orderChange={handleOrderChange}
                      ref={orderProcessDetailsRef}
                      showNoServiceAvailableMessage={orderHasSomeCountryWithNoSearchClassPrices()}
                    />
                  );
                case 2:
                  return (
                    <OrderProcessOwner
                      countries={props.countries}
                      addresses={addresses}
                      labels={{ ...props.labels }}
                      order={currentOrderState}
                      orderChange={handleOrderChange}
                      addressesChange={async () => {
                        const addresses = await getAddresses();
                        setAddresses(addresses);
                      }}
                      ref={orderProcessOwnerRef}
                    />
                  );
                case 3:
                  return (
                    <OrderProcessPayment
                      ref={orderProcessPaymentRef}
                      countries={props.countries}
                      addresses={addresses}
                      termsLink={getRoute('/terms')}
                      labels={{ ...props.labels }}
                      order={currentOrderState}
                      billingBankWireInformation={
                        props.billingBankWireInformation
                      }
                      handlePaymentError={(error: StripeError) => {
                        logError([error.message], 'handlePaymentError');
                      }}
                      onTabChange={(tabName: string) => {
                        const currentState = { ...state };
                        currentState.currentPaymentMethod = tabName as any;
                        setState({ ...currentState });
                      }}
                      orderChange={handleOrderChange}
                      addressesChange={async () => {
                        const addresses = await getAddresses();
                        setAddresses(addresses);
                      }}
                    />
                  );
                default:
                  return null;
              }
            })()}
          </div>
          {/* Step content */}

          <OrderProcessBottomNavBar
            totalAmount={currentOrderState.totalPrice || 0}
            prevLabel={props.labels.backButton}
            nextLabel={
              state.currentStep === 3
                ? props.labels.payButton
                : props.labels.nextButton
            }
            onPrev={handlePrev}
            onNext={handleNext}
            canGoPrev={state.currentStep > 1}
            canGoNext={true}
          />
        </div>
      </div>
    </div>
  );
};

TrademarkSearchPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ITrademarkSearchPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'stepOneTitle',
    'populate[2]': 'stepTwoTitle',
    'populate[3]': 'stepThreeTitle',
    'populate[4]': 'countrySelectionTitle',
    'populate[5]': 'trademarkInformationTitle',
    'populate[6]': 'uploadLogoTip',
    'populate[7]': 'goodsAndServicesTitle',
    'populate[8]': 'goodsAndServicesDescription',
    'populate[9]': 'goodsAndServicesNotice',
    'populate[10]': 'goodsAndServicesButtonLabel',
    'populate[11]': 'goodsAndServicesButtonTip',
    'populate[12]': 'ownerTitle',
    'populate[13]': 'ownerDescription',
    'populate[14]': 'addressesEmptyPlaceholder',
    'populate[15]': 'ownerCompanyLabel',
    'populate[16]': 'ownerTaxIdLabel',
    'populate[17]': 'ownerAddressesTitle',
    'populate[18]': 'ownerAddAddressButton',
    'populate[19]': 'orderSummaryTitle',
    'populate[20]': 'billingTitle',
    'populate[21]': 'billingCreditCardTabName',
    'populate[22]': 'billingBankWireTabName',
    'populate[23]': 'billingBankWireNotice',
    'populate[24]': 'billingBankWireBeneficiaryLabel',
    'populate[25]': 'billingBankWireBeneficiary',
    'populate[26]': 'billingBankWireAddressLabel',
    'populate[27]': 'billingBankWireAddress',
    'populate[28]': 'totalLabel',
    'populate[29]': 'backButton',
    'populate[30]': 'nextButton',
    'populate[31]': 'payButton',
    'populate[32]': 'countrySelectionAddButton',
    'populate[33]': 'trademarkInformationPlaceholder',
    'populate[34]': 'ownerCompanyPlaceholder',
    'populate[35]': 'ownerTaxIdPlaceholder',
    'populate[36]': 'goodsAndServicesClassLabel',
    'populate[37]': 'addressSaveSuccessMessage',
    'populate[38]': 'addressSaveErrorMessage',
    'populate[39]': 'addressDeletePromptMessage',
    'populate[40]': 'addressDeleteSuccessMessage',
    'populate[41]': 'addressDeleteErrorMessage',
    'populate[42]': 'addressEditButton',
    'populate[43]': 'addressDeleteButton',
    'populate[44]': 'countryRemoveButton',
    'populate[45]': 'uploadLogoTitle',
    'populate[46]': 'uploadLogoRemoveButton',
    'populate[47]': 'goodsAndServicesSelectedTitle',
    'populate[48]': 'goodsAndServicesRemoveButton',
    'populate[49]': 'goodsAndServicesModalTitle',
    'populate[50]': 'goodsAndServicesModalDescription',
    'populate[51]': 'goodsAndServicesModalDoneButton',
    'populate[52]': 'goodsAndServicesClassDescripionPlaceholder',
    'populate[53]': 'billingBankWireClipboardTip',
    'populate[54]': 'billingBankWireInformation',
    'populate[55]': 'billingBankWireInformation.title',
    'populate[56]': 'billingBankWireInformation.bankLabel',
    'populate[57]': 'billingBankWireInformation.bankCodeLabel',
    'populate[58]': 'billingBankWireInformation.accountLabel',
    'populate[59]': 'billingBankWireInformation.routingLabel',
    'populate[60]': 'billingBankWireInformation.ibanLabel',
    'populate[61]': 'billingBankWireInformation.ukSortCodeLabel',
    'populate[62]': 'countrySelectionPlaceholder',
    'populate[63]': 'termsCheckboxLabel',
    'populate[64]': 'billingPaymentMethodsTitle',
    'populate[65]': 'addressBookCreateOrModify',
    'populate[66]': 'goodsAndServicesSearchPlaceholder',
    'populate[67]': 'billingBankWireNoticeLink',
    'populate[68]': 'billingBankWireNoticeLinkLabel',
    'populate[69]': 'noServiceAvailableMessage',
  });

  const countriesUrlParams = {
    'populate[0]': 'nameTranslations',
    'fields[0]': 'id',
    'fields[1]': 'iso',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  };

  const classesUrlParams = {
    'populate[0]': 'descriptionTranslations',
    'fields[0]': 'id',
    'fields[1]': 'code',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  };

  const page = (
    await fetchStrapi<any>(
      `/trademark-search-order-page`,
      pageUrlParams,
      buildApiKey
    )
  ).data.attributes as ICheckoutPage;

  const cleanUpRawCountries = (countries: ICountry[]) => {
    return countries.map((x) => {
      const country = { ...x };
      country.nameTranslations = x.nameTranslations.map((y) => {
        const translation = { ...y };
        delete translation.id;
        return translation;
      });
      return country;
    }) as any[];
  };

  const countriesRaw = await fetchStrapi<any>(
    `/countries`,
    countriesUrlParams,
    buildApiKey
  );
  const countries = cleanUpRawCountries(
    flatStrapiCollection(countriesRaw) as ICountry[]
  ) as ICountry[];
  const classes = flatStrapiCollection(
    await fetchStrapi<any>(`/classes`, classesUrlParams, buildApiKey)
  ) as IClass[];
  const commonTranslations = await getCommonTranslations();

  const labels = {
    stepOneTitle: getTranslation(page.stepOneTitle, context.locale)
      ?.text as string,
    stepTwoTitle: getTranslation(page.stepTwoTitle, context.locale)
      ?.text as string,
    stepThreeTitle: getTranslation(page.stepThreeTitle, context.locale)
      ?.text as string,
    countrySelectionTitle: getTranslation(
      page.countrySelectionTitle,
      context.locale
    )?.text as string,
    countrySelectionPlaceholder: getTranslation(
      page.countrySelectionPlaceholder,
      context.locale
    )?.text as string,
    countrySelectionAddButton: getTranslation(
      page.countrySelectionAddButton,
      context.locale
    )?.text as string,
    trademarkInformationTitle: getTranslation(
      page.trademarkInformationTitle,
      context.locale
    )?.text as string,
    trademarkInformationPlaceholder: getTranslation(
      page.trademarkInformationPlaceholder,
      context.locale
    )?.text as string,
    uploadLogoTip: getTranslation(page.uploadLogoTip, context.locale)
      ?.text as string,
    goodsAndServicesTitle: getTranslation(
      page.goodsAndServicesTitle,
      context.locale
    )?.text as string,
    goodsAndServicesDescription: getTranslation(
      page.goodsAndServicesDescription,
      context.locale
    )?.text as string,
    goodsAndServicesNotice: getTranslation(
      page.goodsAndServicesNotice,
      context.locale
    )?.text as string,
    goodsAndServicesButtonLabel: getTranslation(
      page.goodsAndServicesButtonLabel,
      context.locale
    )?.text as string,
    goodsAndServicesButtonTip: getTranslation(
      page.goodsAndServicesButtonTip,
      context.locale
    )?.text as string,
    ownerTitle: getTranslation(page.ownerTitle, context.locale)?.text as string,
    ownerDescription: getTranslation(page.ownerDescription, context.locale)
      ?.text as string,
    addressesEmptyPlaceholder: getTranslation(
      page.addressesEmptyPlaceholder,
      context.locale
    )?.text as string,
    ownerCompanyLabel: getTranslation(page.ownerCompanyLabel, context.locale)
      ?.text as string,
    ownerCompanyPlaceholder: getTranslation(
      page.ownerCompanyPlaceholder,
      context.locale
    )?.text as string,
    ownerTaxIdLabel: getTranslation(page.ownerTaxIdLabel, context.locale)
      ?.text as string,
    ownerTaxIdPlaceholder: getTranslation(
      page.ownerTaxIdPlaceholder,
      context.locale
    )?.text as string,
    ownerAddressesTitle: getTranslation(
      page.ownerAddressesTitle,
      context.locale
    )?.text as string,
    ownerAddAddressButton: getTranslation(
      page.ownerAddAddressButton,
      context.locale
    )?.text as string,
    orderSummaryTitle: getTranslation(page.orderSummaryTitle, context.locale)
      ?.text as string,
    billingTitle: getTranslation(page.billingTitle, context.locale)
      ?.text as string,
    billingCreditCardTabName: getTranslation(
      page.billingCreditCardTabName,
      context.locale
    )?.text as string,
    billingBankWireTabName: getTranslation(
      page.billingBankWireTabName,
      context.locale
    )?.text as string,
    billingBankWireNotice: getTranslation(
      page.billingBankWireNotice,
      context.locale
    )?.text as string,
    billingBankWireNoticeLink: getTranslation(
      page.billingBankWireNoticeLink,
      context.locale
    )?.text as string,
    billingBankWireNoticeLinkLabel: getTranslation(
      page.billingBankWireNoticeLinkLabel,
      context.locale
    )?.text as string,
    billingBankWireBeneficiaryLabel: getTranslation(
      page.billingBankWireBeneficiaryLabel,
      context.locale
    )?.text as string,
    billingBankWireBeneficiary: getTranslation(
      page.billingBankWireBeneficiary,
      context.locale
    )?.text as string,
    billingBankWireAddressLabel: getTranslation(
      page.billingBankWireAddressLabel,
      context.locale
    )?.text as string,
    billingBankWireAddress: getTranslation(
      page.billingBankWireAddress,
      context.locale
    )?.text as string,
    totalLabel: getTranslation(page.totalLabel, context.locale)?.text as string,
    backButton: getTranslation(page.backButton, context.locale)?.text as string,
    nextButton: getTranslation(page.nextButton, context.locale)?.text as string,
    payButton: getTranslation(page.payButton, context.locale)?.text as string,
    goodsAndServicesClassLabel: getTranslation(
      page.goodsAndServicesClassLabel,
      context.locale
    ).text as string,
    addressSaveSuccessMessage: getTranslation(
      page.addressSaveSuccessMessage,
      context.locale
    ).text as string,
    addressSaveErrorMessage: getTranslation(
      page.addressSaveErrorMessage,
      context.locale
    ).text as string,
    addressDeletePromptMessage: getTranslation(
      page.addressDeletePromptMessage,
      context.locale
    ).text as string,
    addressDeleteSuccessMessage: getTranslation(
      page.addressDeleteSuccessMessage,
      context.locale
    ).text as string,
    addressDeleteErrorMessage: getTranslation(
      page.addressDeleteErrorMessage,
      context.locale
    ).text as string,
    addressEditButton: getTranslation(page.addressEditButton, context.locale)
      .text as string,
    addressDeleteButton: getTranslation(
      page.addressDeleteButton,
      context.locale
    ).text as string,
    countryRemoveButton: getTranslation(
      page.countryRemoveButton,
      context.locale
    ).text as string,
    uploadLogoTitle: getTranslation(page.uploadLogoTitle, context.locale)
      .text as string,
    uploadLogoRemoveButton: getTranslation(
      page.uploadLogoRemoveButton,
      context.locale
    ).text as string,
    goodsAndServicesSelectedTitle: getTranslation(
      page.goodsAndServicesSelectedTitle,
      context.locale
    ).text as string,
    goodsAndServicesRemoveButton: getTranslation(
      page.goodsAndServicesRemoveButton,
      context.locale
    ).text as string,
    goodsAndServicesModalTitle: getTranslation(
      page.goodsAndServicesModalTitle,
      context.locale
    ).text as string,
    goodsAndServicesModalDescription: getTranslation(
      page.goodsAndServicesModalDescription,
      context.locale
    ).text as string,
    goodsAndServicesModalCancelButton: getTranslation(
      page.goodsAndServicesModalCancelButton,
      context.locale
    ).text as string,
    goodsAndServicesModalDoneButton: getTranslation(
      page.goodsAndServicesModalDoneButton,
      context.locale
    ).text as string,
    goodsAndServicesClassDescripionPlaceholder: getTranslation(
      page.goodsAndServicesClassDescripionPlaceholder,
      context.locale
    ).text as string,
    goodsAndServicesSearchPlaceholder: getTranslation(
      page.goodsAndServicesSearchPlaceholder,
      context.locale
    ).text as string,
    billingBankWireClipboardTip: getTranslation(
      page.billingBankWireClipboardTip,
      context.locale
    ).text as string,
    termsCheckboxLabel: getTranslation(page.termsCheckboxLabel, context.locale)
      .text as string,
    billingPaymentMethodsTitle: getTranslation(
      page.billingPaymentMethodsTitle,
      context.locale
    ).text as string,
    addressBookCreateOrModify: getTranslation(
      page.addressBookCreateOrModify,
      context.locale || 'en'
    ),
    formValidation: {
      requiredField: getTranslation(
        commonTranslations.requiredField,
        context.locale
      ).text as string,
      termsAndConditionsMustBeAccepted: getTranslation(
        commonTranslations.termsAndConditionsMustBeAccepted,
        context.locale
      ).text as string,
    },
    showMore: getTranslation(commonTranslations.showMore, context.locale)
      .text as string,
    showLess: getTranslation(commonTranslations.showLess, context.locale)
      .text as string,
    noServiceAvailableMessage: getTranslation(
      page.noServiceAvailableMessage,
      context.locale
    ).text as string,
  };

  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/trademark-search', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const props: ITrademarkSearchPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    alternativeLinks,
    canonicalLink,
    countries,
    classes,
    labels,
    billingBankWireInformation: page.billingBankWireInformation,
  };

  return {
    props,
  };
}

export default TrademarkSearchPage;
